'use client';

import React, { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import VerifiedBadge from '@/components/VerifiedBadge';
import { useAuth } from '@/contexts/AuthContext';
import { FARM_EQUIPMENT_LABEL, GRAIN_FEED_LABEL } from '@/lib/features';
import SubscriptionPanel from '@/components/SubscriptionPanel';
import { createClient } from '@/lib/supabase/client';
import {
  fetchListings,
  withdrawListing,
  markListingSold,
  type Listing,
} from '@/lib/listings';
import { fetchMyBuyerRequests, type BuyerRequest } from '@/lib/buyerRequests';
import { fetchMyBidLots, money as auctionMoney, timeLeft, type AuctionLot } from '@/lib/auctions';
import {
  fetchBoostAllowance,
  boostListing,
  boostAllowanceLabel,
  type BoostAllowance,
} from '@/lib/limits';

type TabId = 'listings' | 'bids' | 'haulage' | 'buyer-requests';
type Category = 'all' | 'livestock' | 'machinery' | 'farm-equipment' | 'crops-feed';
type ListingStatus = 'active' | 'sold' | 'expired' | 'pending';
// The bid and haulage-job shapes that used to live here went with the mock data.
// They will come back off the real tables when auctions and the job board are
// built, rather than being guessed at now.
// 'pending' matters: buyer_requests_enforce_review holds every new request for
// approval, so without it a request posted a minute ago reads as "Expired".
type RequestStatus = 'active' | 'pending' | 'fulfilled' | 'expired';

interface MyListing {
  id: string;
  title: string;
  category: Exclude<Category, 'all'>;
  price: number;
  priceUnit: string;
  status: ListingStatus;
  views: number;
  enquiries: number;
  postedDate: string;
  image: string;
  alt: string;
  location: string;
  /** Kept from the row so the card can offer the right actions. */
  priceType: string | null;
  isFeatured: boolean;
  boostedUntil: string | null;
  expiresAt: string | null;
  /** The real database status, before it is squashed into the four swatches. */
  rawStatus: string;
}


interface MyBuyerRequest {
  id: string;
  title: string;
  category: Exclude<Category, 'all'>;
  description: string;
  budget: number | null;
  budgetUnit: string;
  location: string;
  status: RequestStatus;
  responses: number;
  postedDate: string;
}

// Rocket generated four arrays of fake activity here — the same "12 Limousin
// Cross Steers" and "John Deere 6130R" for every member who logged in. That is
// worse than an empty dashboard, because a real member reads it as their own.
// Everything below now comes from the member's own rows.

/** listings.category in the database vs the filter ids this screen uses. */
const CATEGORY_FROM_DB: Record<string, Exclude<Category, 'all'>> = {
  livestock: 'livestock',
  machinery: 'machinery',
  deadstock: 'farm-equipment',
  'crops-feed': 'crops-feed',
  haulage: 'machinery',
};

function categoryOf(dbCategory: string | null): Exclude<Category, 'all'> {
  return CATEGORY_FROM_DB[dbCategory ?? ''] ?? 'farm-equipment';
}

/** The dashboard only has swatches for four statuses; map the rest onto them. */
function statusOf(dbStatus: string | null): ListingStatus {
  switch (dbStatus) {
    case 'active': return 'active';
    case 'sold': return 'sold';
    case 'expired':
    case 'withdrawn':
    case 'declined': return 'expired';
    default: return 'pending';
  }
}

const PRICE_UNIT: Record<string, string> = {
  perhead: '/head',
  perkg: '/kg',
};

function shortDate(iso: string | null): string {
  if (!iso) return 'recently';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function toMyListing(l: Listing): MyListing {
  const images = [...(l.listing_images ?? [])].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
  );
  return {
    id: l.id,
    title: l.title,
    category: categoryOf(l.category),
    price: l.price ?? 0,
    priceUnit: PRICE_UNIT[l.price_type ?? ''] ?? '',
    status: statusOf(l.status),
    views: l.view_count ?? 0,
    enquiries: l.enquiry_count ?? 0,
    postedDate: shortDate(l.created_at),
    image: images[0]?.image_url ?? '',
    alt: l.title,
    location: [l.location_town, l.county].filter(Boolean).join(', ') || 'Location not given',
    priceType: l.price_type ?? null,
    isFeatured: Boolean(l.is_featured),
    boostedUntil: l.boosted_until ?? null,
    expiresAt: l.expires_at ?? null,
    rawStatus: l.status ?? 'pending',
  };
}

function toMyBuyerRequest(r: BuyerRequest): MyBuyerRequest {
  return {
    id: r.id,
    title: r.title,
    category: categoryOf(r.category),
    description: r.description ?? '',
    budget: r.budget_max ?? r.budget_min ?? null,
    budgetUnit: '',
    location: r.county ?? 'Anywhere',
    status:
      r.status === 'open' ? 'active' :
      r.status === 'pending' ? 'pending' :
      r.status === 'fulfilled' ? 'fulfilled' :
      'expired',
    responses: r.response_count ?? 0,
    postedDate: shortDate(r.created_at),
  };
}


const categoryLabels: Record<Exclude<Category, 'all'>, string> = {
  livestock: 'Livestock',
  machinery: 'Machinery',
  'farm-equipment': FARM_EQUIPMENT_LABEL,
  'crops-feed': GRAIN_FEED_LABEL
};

const categoryColors: Record<Exclude<Category, 'all'>, string> = {
  livestock: 'bg-green-100 text-green-800',
  machinery: 'bg-blue-100 text-blue-800',
  'farm-equipment': 'bg-amber-100 text-amber-800',
  'crops-feed': 'bg-yellow-100 text-yellow-800'
};

const statusConfig: Record<ListingStatus, {label: string;color: string;}> = {
  active: { label: 'Active', color: 'bg-green-100 text-green-700' },
  sold: { label: 'Sold', color: 'bg-gray-100 text-gray-600' },
  expired: { label: 'Expired', color: 'bg-red-100 text-red-600' },
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700' }
};


const requestStatusConfig: Record<RequestStatus, {label: string;color: string;}> = {
  active: { label: 'Active', color: 'bg-green-100 text-green-700' },
  pending: { label: 'Awaiting Review', color: 'bg-yellow-100 text-yellow-700' },
  fulfilled: { label: 'Fulfilled', color: 'bg-gray-100 text-gray-600' },
  expired: { label: 'Expired', color: 'bg-red-100 text-red-600' }
};

// Auctions and the haulage job board are not built yet, so those two tabs have
// nothing real behind them. They stay visible, but say so rather than showing
// invented bids and jobs.
const BIDS_READY = true;
const HAULAGE_READY = false;


const categoryFilters: {id: Category;label: string;}[] = [
{ id: 'all', label: 'All Categories' },
{ id: 'livestock', label: 'Livestock' },
{ id: 'machinery', label: 'Machinery' },
{ id: 'farm-equipment', label: FARM_EQUIPMENT_LABEL },
{ id: 'crops-feed', label: GRAIN_FEED_LABEL }];


export default function MyDashboardScreen() {
  const { user, profile, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState<TabId>('listings');
  const [categoryFilter, setCategoryFilter] = useState<Category>('all');

  const [listings, setListings] = useState<MyListing[]>([]);
  const [requests, setRequests] = useState<MyBuyerRequest[]>([]);
  const [boosts, setBoosts] = useState<BoostAllowance | null>(null);
  const [myBids, setMyBids] = useState<{lot: AuctionLot; yourMax: number; leading: boolean;}[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      setListings([]);
      setRequests([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const supabase = createClient();
      // status: null so the member sees their pending, sold and expired ones too
      // — this is their own record, not the public board.
      const [mine, myRequests, allowance, bidLots] = await Promise.all([
        fetchListings(supabase, { sellerId: user.id, status: null, limit: 200, featuredFirst: false }),
        fetchMyBuyerRequests(supabase, user.id).catch(() => [] as BuyerRequest[]),
        fetchBoostAllowance(supabase).catch(() => null),
        fetchMyBidLots(supabase, user.id).catch(() => []),
      ]);
      setListings(mine.listings.map(toMyListing));
      setRequests(myRequests.map(toMyBuyerRequest));
      setBoosts(allowance);
      setMyBids(bidLots);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Could not load your dashboard. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading) load();
  }, [authLoading, load]);

  const act = async (id: string, fn: () => Promise<void>) => {
    setBusyId(id);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const handleWithdraw = (l: MyListing) =>
    act(l.id, async () => {
      const supabase = createClient();
      await withdrawListing(supabase, l.id);
    });

  const handleSold = (l: MyListing) =>
    act(l.id, async () => {
      const supabase = createClient();
      await markListingSold(supabase, l.id);
    });

  const handleBoost = (l: MyListing) =>
    act(l.id, async () => {
      const supabase = createClient();
      const result = await boostListing(supabase, l.id, 7);
      if (!result.boosted) {
        throw new Error(
          result.reason === 'plan'
            ? 'Boosts are not included on your plan — upgrade to use them.'
            : "You have used all of this month's boosts."
        );
      }
    });

  const byCategory = <T extends {category: Exclude<Category, 'all'>;}>(rows: T[]) =>
    categoryFilter === 'all' ? rows : rows.filter((r) => r.category === categoryFilter);

  const filteredListings = byCategory(listings);
  const filteredRequests = byCategory(requests);

  const liveCount = listings.filter((l) => l.rawStatus === 'active').length;
  const pendingCount = listings.filter((l) => l.rawStatus === 'pending').length;

  const tabs: {id: TabId;label: string;icon: string;count: number;}[] = [
  { id: 'listings', label: 'My Listings', icon: 'TagIcon', count: listings.length },
  { id: 'bids', label: 'Active Bids', icon: 'BellAlertIcon', count: myBids.filter((b) => b.lot.status === 'live').length },
  { id: 'haulage', label: 'Haulage Jobs', icon: 'TruckIcon', count: 0 },
  { id: 'buyer-requests', label: 'Buyer Requests', icon: 'ShoppingCartIcon', count: requests.length }];

  const statsData = [
  { label: 'Live Listings', value: liveCount, icon: 'TagIcon', color: 'text-green-600', bg: 'bg-green-50' },
  { label: 'Awaiting Review', value: pendingCount, icon: 'ClockIcon', color: 'text-amber-600', bg: 'bg-amber-50' },
  { label: 'Total Enquiries', value: listings.reduce((n, l) => n + l.enquiries, 0), icon: 'ChatBubbleLeftIcon', color: 'text-blue-600', bg: 'bg-blue-50' },
  { label: 'Buyer Requests', value: requests.filter((r) => r.status === 'active' || r.status === 'pending').length, icon: 'ShoppingCartIcon', color: 'text-purple-600', bg: 'bg-purple-50' }];


  return (
    <main className="min-h-screen bg-gray-50">
      {/* Header bar */}
      <div className="bg-primary text-white py-8 px-4">
        <div className="max-w-screen-xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Icon name="UserCircleIcon" size={22} />
                <h1 className="text-2xl font-bold tracking-tight">My Dashboard</h1>
                {profile?.is_verified && <VerifiedBadge size="sm" />}
              </div>
              <p className="text-green-200 text-sm">
                {profile?.full_name || profile?.business_name || user?.email || 'Welcome back'}
                {profile?.county ? ` · ${profile.county}` : ''}
              </p>
            </div>
            <div className="flex gap-2">
              <Link
                href="/post-listing"
                className="flex items-center gap-2 bg-accent hover:bg-accent/90 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors">
                
                <Icon name="PlusCircleIcon" size={16} />
                Post Listing
              </Link>
              <Link
                href="/haulage-job-board"
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors">
                
                <Icon name="TruckIcon" size={16} />
                Post Haulage Job
              </Link>
            </div>
          </div>

          {/* Stats strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            {statsData.map((stat) =>
            <div key={stat.label} className="bg-white/10 rounded-xl px-4 py-3 flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg ${stat.bg} flex items-center justify-center shrink-0`}>
                  <Icon name={stat.icon as Parameters<typeof Icon>[0]['name']} size={18} className={stat.color} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white leading-none">{stat.value}</p>
                  <p className="text-green-200 text-xs mt-0.5">{stat.label}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-screen-xl mx-auto px-4 py-6">
        {/* Membership, reveal allowance and the auto-renewal switch */}
        <div className="mb-5">
          <SubscriptionPanel />
        </div>

        {error &&
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-5 py-4 mb-5" role="alert">
            <Icon name="ExclamationTriangleIcon" size={20} className="text-red-600 shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        }

        {!user && !authLoading &&
        <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-5 py-4 mb-5">
            <Icon name="InformationCircleIcon" size={20} className="text-blue-600 shrink-0 mt-0.5" />
            <p className="text-sm text-blue-900">
              <span className="font-bold">You&apos;re not signed in.</span>{' '}
              <Link href="/sign-up-login-screen" className="underline font-semibold">Sign in</Link>{' '}
              to see your listings, enquiries and buyer requests.
            </p>
          </div>
        }

        {user && boosts && boosts.limit !== 0 &&
        <p className="text-xs text-gray-500 mb-4">{boostAllowanceLabel(boosts)}.</p>
        }

        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-xl border border-gray-200 p-1 mb-5 overflow-x-auto">
          {tabs.map((tab) =>
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap transition-all flex-1 justify-center ${
            activeTab === tab.id ?
            'bg-primary text-white shadow-sm' :
            'text-gray-600 hover:bg-gray-50'}`
            }>
            
              <Icon name={tab.icon as Parameters<typeof Icon>[0]['name']} size={16} />
              {tab.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
            activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`
            }>
                {tab.count}
              </span>
            </button>
          )}
        </div>

        {/* Category filter — not shown for haulage tab */}
        {activeTab !== 'haulage' &&
        <div className="flex gap-2 mb-5 flex-wrap">
            {categoryFilters.map((cat) =>
          <button
            key={cat.id}
            onClick={() => setCategoryFilter(cat.id)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all border ${
            categoryFilter === cat.id ?
            'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:border-primary/40'}`
            }>
            
                {cat.label}
              </button>
          )}
          </div>
        }

        {/* ── MY LISTINGS ── */}
        {activeTab === 'listings' &&
        <div className="space-y-3">
            {loading &&
          <SkeletonRows />
          }
            {!loading && filteredListings.length === 0 &&
          <EmptyState icon="TagIcon" message="No listings found in this category." cta="Post a Listing" href="/post-listing" />
          }
            {!loading && filteredListings.map((listing) =>
          <div key={listing.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col sm:flex-row hover:shadow-md transition-shadow">
                <div className="sm:w-36 h-32 sm:h-auto shrink-0 overflow-hidden bg-gray-100 flex items-center justify-center">
                  {listing.image ?
                <img src={listing.image} alt={listing.alt} className="w-full h-full object-cover" /> :

                <Icon name="PhotoIcon" size={26} className="text-gray-300" />
                }
                </div>
                <div className="flex-1 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${categoryColors[listing.category]}`}>
                        {categoryLabels[listing.category]}
                      </span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusConfig[listing.status].color}`}>
                        {listing.rawStatus === 'withdrawn' ? 'Removed' : statusConfig[listing.status].label}
                      </span>
                      {listing.isFeatured &&
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                          {listing.boostedUntil ? 'Boosted' : 'Featured'}
                        </span>
                    }
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm leading-snug truncate">{listing.title}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {listing.location} · Posted {listing.postedDate}
                      {listing.rawStatus === 'active' && listing.expiresAt ?
                    ` · Live until ${shortDate(listing.expiresAt)}` :
                    ''}
                    </p>
                    <div className="flex items-center gap-4 mt-2">
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <Icon name="EyeIcon" size={13} /> {listing.views} views
                      </span>
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <Icon name="ChatBubbleLeftIcon" size={13} /> {listing.enquiries} enquiries
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <p className="text-lg font-bold text-primary">
                      {listing.priceType === 'poa' ?
                    'POA' :
                    <>£{listing.price.toLocaleString()}<span className="text-xs font-normal text-gray-500">{listing.priceUnit}</span></>
                    }
                    </p>
                    <div className="flex flex-wrap gap-2 justify-end">
                      <Link
                    href={`/listing/${listing.id}`}
                    className="flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-primary border border-gray-200 hover:border-primary/40 px-2.5 py-1.5 rounded-lg transition-all">

                        <Icon name="EyeIcon" size={13} /> View
                      </Link>
                      {listing.rawStatus === 'active' && !listing.isFeatured &&
                  <button
                    onClick={() => handleBoost(listing)}
                    disabled={busyId === listing.id}
                    className="flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-primary border border-gray-200 hover:border-primary/40 px-2.5 py-1.5 rounded-lg transition-all disabled:opacity-50">

                          <Icon name="ArrowTrendingUpIcon" size={13} /> Boost
                        </button>
                  }
                      {listing.rawStatus === 'active' &&
                  <button
                    onClick={() => handleSold(listing)}
                    disabled={busyId === listing.id}
                    className="flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-primary border border-gray-200 hover:border-primary/40 px-2.5 py-1.5 rounded-lg transition-all disabled:opacity-50">

                          <Icon name="CheckCircleIcon" size={13} /> Mark sold
                        </button>
                  }
                      {(listing.rawStatus === 'active' || listing.rawStatus === 'pending') &&
                  <button
                    onClick={() => handleWithdraw(listing)}
                    disabled={busyId === listing.id}
                    className="flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-600 border border-gray-200 hover:border-red-200 px-2.5 py-1.5 rounded-lg transition-all disabled:opacity-50">

                          <Icon name="TrashIcon" size={13} /> Remove
                        </button>
                  }
                    </div>
                  </div>
                </div>
              </div>
          )}
          </div>
        }

        {/* ── ACTIVE BIDS ── */}
        {activeTab === 'bids' &&
        <div className="space-y-3">
            {myBids.length === 0 ?
          <EmptyState icon="BellAlertIcon" message="You have not bid on anything yet." cta="Browse Auctions" href="/timed-auctions" /> :

          myBids.map(({ lot, yourMax, leading }) =>
          <div key={lot.id} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                lot.status === 'live' ? leading ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-800' :
                lot.status === 'sold' ? leading ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600' :
                'bg-gray-100 text-gray-600'}`
                }>
                        {lot.status === 'live' ? leading ? 'Winning' : 'Outbid' :
                  lot.status === 'sold' ? leading ? 'You won' : 'Sold to another bidder' :
                  'Closed, reserve not met'}
                      </span>
                      {lot.status === 'live' &&
                <span className="text-xs text-gray-500">{timeLeft(lot.closes_at)} left</span>
                }
                    </div>
                    <a href={`/timed-auctions/lot/${lot.id}`} className="font-semibold text-gray-900 text-sm hover:text-primary">
                      {lot.title}
                    </a>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {[lot.location_town, lot.county].filter(Boolean).join(', ') || 'Location not given'}
                    </p>
                  </div>
                  <div className="flex gap-6 shrink-0">
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Standing at</p>
                      <p className="text-lg font-bold text-primary">{auctionMoney(lot.current_bid)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Your maximum</p>
                      <p className="text-sm font-semibold text-gray-700">{auctionMoney(yourMax)}</p>
                    </div>
                  </div>
                </div>
              </div>
          )
          }
          </div>
        }

        {/* ── HAULAGE JOBS ── */}
        {activeTab === 'haulage' &&
        <div className="space-y-3">
            {!HAULAGE_READY ?
          <ComingSoon
            icon="TruckIcon"
            title="The haulage job board is being built"
            message="Once it is live, the jobs you post will show here with the bids hauliers have put in against them." /> :

          <EmptyState icon="TruckIcon" message="You haven't posted any haulage jobs yet." cta="Post a Haulage Job" href="/haulage-job-board" />
          }
          </div>
        }

        {/* ── BUYER REQUESTS ── */}
        {activeTab === 'buyer-requests' &&
        <div className="space-y-3">
            {loading &&
          <SkeletonRows />
          }
            {!loading && filteredRequests.length === 0 &&
          <EmptyState icon="ShoppingCartIcon" message="No buyer requests found in this category." cta="Post a Buyer Request" href="/post-listing" />
          }
            {!loading && filteredRequests.map((req) =>
          <div key={req.id} className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${categoryColors[req.category]}`}>
                        {categoryLabels[req.category]}
                      </span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${requestStatusConfig[req.status].color}`}>
                        {requestStatusConfig[req.status].label}
                      </span>
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm">{req.title}</h3>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">{req.description}</p>
                    <div className="flex flex-wrap items-center gap-3 mt-2">
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <Icon name="MapPinIcon" size={12} /> {req.location}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <Icon name="CalendarDaysIcon" size={12} /> Posted {req.postedDate}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    {req.budget &&
                <div className="text-right">
                        <p className="text-xs text-gray-500">Budget</p>
                        <p className="text-lg font-bold text-primary">
                          £{req.budget.toLocaleString()}<span className="text-xs font-normal text-gray-500">{req.budgetUnit}</span>
                        </p>
                      </div>
                }
                    <div className="text-right">
                      <p className="text-2xl font-bold text-blue-600">{req.responses}</p>
                      <p className="text-xs text-gray-500">responses</p>
                    </div>
                    <Link
                  href="/buyer-requests"
                  className="flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-primary border border-gray-200 hover:border-primary/40 px-2.5 py-1.5 rounded-lg transition-all">

                      <Icon name="EyeIcon" size={13} /> View responses
                    </Link>
                  </div>
                </div>
              </div>
          )}
          </div>
        }
      </div>
    </main>);

}

/**
 * For a tab whose feature genuinely is not built yet.
 *
 * Deliberately different from EmptyState: "you have no bids" and "auctions do
 * not exist yet" are not the same message, and a member who reads the first one
 * while the second is true goes looking for a page that isn't there.
 */
function ComingSoon({ icon, title, message }: {icon: string;title: string;message: string;}) {
  return (
    <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 flex flex-col items-center gap-3 text-center">
      <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center">
        <Icon name={icon as Parameters<typeof Icon>[0]['name']} size={26} className="text-gray-400" />
      </div>
      <p className="font-semibold text-gray-700 text-sm">{title}</p>
      <p className="text-gray-500 text-sm max-w-md">{message}</p>
    </div>);

}

function SkeletonRows() {
  return (
    <div className="space-y-3" aria-hidden="true">
      {[0, 1, 2].map((i) =>
      <div key={i} className="bg-white rounded-xl border border-gray-200 h-28 animate-pulse" />
      )}
    </div>);

}

function EmptyState({ icon, message, cta, href }: {icon: string;message: string;cta: string;href: string;}) {
  return (
    <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 flex flex-col items-center gap-3 text-center">
      <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center">
        <Icon name={icon as Parameters<typeof Icon>[0]['name']} size={26} className="text-gray-400" />
      </div>
      <p className="text-gray-500 text-sm">{message}</p>
      <Link href={href} className="text-sm font-semibold text-primary hover:underline">{cta} →</Link>
    </div>);

}